import { movies } from "@/mocks/movies";
import { cinemas } from "@/mocks/cinemas";
import { news } from "@/mocks/news";
import type { Cinema, Movie, NewsArticle, Schedule } from "@/types/cinema";

export const scheduleDates = [
  "2026-10-15",
  "2026-10-16",
  "2026-10-17",
  "2026-10-18",
  "2026-10-19",
];

const times = ["10:30", "13:15", "16:00", "19:20", "21:40"];
const halls = ["1 号厅", "2 号厅", "3 号厅", "IMAX 厅", "杜比全景声厅"];
const languages = ["国语", "国语", "原版", "国语"];

const nowMovies = movies.filter((m) => m.status === "now");

function buildSchedules(): Schedule[] {
  const list: Schedule[] = [];
  cinemas.forEach((cinema, ci) => {
    scheduleDates.forEach((date, di) => {
      nowMovies.forEach((movie, mi) => {
        const seed = ci * 7 + di * 3 + mi;
        const sessionCount = 2 + (seed % 2);
        for (let s = 0; s < sessionCount; s += 1) {
          const tIdx = (seed + s * 2) % times.length;
          const hIdx = (seed + s) % halls.length;
          const lIdx = (seed + s) % languages.length;
          list.push({
            id: `sc-${cinema.id}-${movie.id}-${di}-${s}`,
            movieId: movie.id,
            cinemaId: cinema.id,
            date,
            time: times[tIdx],
            hall: halls[hIdx],
            language: languages[lIdx],
            price: 38 + ((seed + s) % 7) * 5,
            format: movie.formats[s % movie.formats.length],
          });
        }
      });
    });
  });
  return list;
}

export const schedules: Schedule[] = buildSchedules();

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getCinemaById(id: string): Cinema | undefined {
  return cinemas.find((c) => c.id === id);
}

export function getNewsById(id: string): NewsArticle | undefined {
  return news.find((n) => n.id === id);
}

export function getScheduleById(id: string): Schedule | undefined {
  return schedules.find((s) => s.id === id);
}

export function getSchedulesByMovie(movieId: string): Schedule[] {
  return schedules.filter((s) => s.movieId === movieId);
}

export function getSchedulesByCinema(cinemaId: string, date?: string): Schedule[] {
  return schedules.filter(
    (s) => s.cinemaId === cinemaId && (!date || s.date === date),
  );
}

export function getCinemaDates(cinemaId: string): string[] {
  const set = new Set(
    schedules.filter((s) => s.cinemaId === cinemaId).map((s) => s.date),
  );
  return scheduleDates.filter((d) => set.has(d));
}

export function getCinemasForMovie(movieId: string): Cinema[] {
  const ids = new Set(
    schedules.filter((s) => s.movieId === movieId).map((s) => s.cinemaId),
  );
  return cinemas.filter((c) => ids.has(c.id));
}

export function getMoviesForCinema(cinemaId: string, date?: string): Movie[] {
  const ids = new Set(
    schedules
      .filter((s) => s.cinemaId === cinemaId && (!date || s.date === date))
      .map((s) => s.movieId),
  );
  return movies.filter((m) => ids.has(m.id));
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .filter((m) => m.status === "now")
    .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
    .slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getUpcomingMovies(limit = 6): Movie[] {
  return movies
    .filter((m) => m.status === "upcoming")
    .sort((a, b) => a.releaseDate.localeCompare(b.releaseDate))
    .slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const byGenre = movies.filter(
    (m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)),
  );
  const rest = movies.filter(
    (m) => m.id !== movie.id && !byGenre.some((b) => b.id === m.id),
  );
  return [...byGenre, ...rest].slice(0, limit);
}

export function formatDateLabel(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  const weeks = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${weeks[d.getDay()]}`;
}